/**
 * Zod validation schemas for employee absence forms
 * Provides form validation and transformation to API request format
 */

import { z } from 'zod';
import type { I18n } from '@lingui/core';
import { format } from 'date-fns';
import type {
  EmployeeAbsence,
  CreateEmployeeAbsenceRequest,
  UpdateEmployeeAbsenceRequest,
} from '@/types/employeeAbsences';
import { AbsenceType, CertificateMode } from '@/types/employeeAbsences';
import { typeError } from '@/lib/zodTypeError';

// Time format regex: HH:MM (00:00 - 23:59)
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Creates the create absence form schema with i18n validation messages.
 *
 * @param i18n - Lingui i18n instance for localized error messages
 * @returns Zod schema for create absence form
 */
export const getCreateAbsenceSchema = (i18n: I18n) =>
  z
    .object({
      employeeId: z.string().min(1, {
        message: i18n._({
          id: 'absences.validation.employeeRequired',
          message: 'Employee is required',
        }),
      }),
      absenceType: z.enum(
        [
          AbsenceType.VACATION,
          AbsenceType.SICK,
          AbsenceType.PERSONAL,
          AbsenceType.TRAINING,
          AbsenceType.OTHER,
          AbsenceType.ACCIDENT,
          AbsenceType.COMP_TIME,
          AbsenceType.CHILD_SICK,
        ],
        {
          error: typeError({
            required: i18n._({
            id: 'absences.validation.typeRequired',
            message: 'Absence type is required',
          }),
          }),
        }
      ),
      fromDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'absences.validation.fromDateRequired',
          message: 'From date is required',
        }),
          invalid: i18n._({
          id: 'absences.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      toDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'absences.validation.toDateRequired',
          message: 'To date is required',
        }),
          invalid: i18n._({
          id: 'absences.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      fullDay: z.boolean().default(true),
      fromTime: z
        .string()
        .regex(TIME_REGEX, {
          message: i18n._({
            id: 'absences.validation.timeFormat',
            message: 'Time must be in HH:MM format',
          }),
        })
        .optional(),
      toTime: z
        .string()
        .regex(TIME_REGEX, {
          message: i18n._({
            id: 'absences.validation.timeFormat',
            message: 'Time must be in HH:MM format',
          }),
        })
        .optional(),
      notes: z.string().optional(),
    })
    .refine(
      (data) => {
        // To date must be on or after from date
        return data.toDate >= data.fromDate;
      },
      {
        message: i18n._({
          id: 'absences.validation.toDateAfterFrom',
          message: 'To date must be on or after from date',
        }),
        path: ['toDate'],
      }
    )
    .refine(
      (data) => {
        // If not full day, both times are required
        if (!data.fullDay) {
          return !!data.fromTime && !!data.toTime;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'absences.validation.timesRequiredForPartialDay',
          message: 'Start and end times are required for partial-day absences',
        }),
        path: ['fromTime'],
      }
    )
    .refine(
      (data) => {
        // End time must be after start time (only for same-day partial absences)
        if (
          !data.fullDay &&
          data.fromTime &&
          data.toTime &&
          data.fromDate.getTime() === data.toDate.getTime()
        ) {
          return data.toTime > data.fromTime;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'absences.validation.endTimeAfterStart',
          message: 'End time must be after start time',
        }),
        path: ['toTime'],
      }
    );

/**
 * Creates the update absence form schema with optimistic locking field.
 *
 * @param i18n - Lingui i18n instance for localized error messages
 * @returns Zod schema for update absence form
 */
export const getUpdateAbsenceSchema = (i18n: I18n) =>
  z
    .object({
      absenceType: z.enum(
        [
          AbsenceType.VACATION,
          AbsenceType.SICK,
          AbsenceType.PERSONAL,
          AbsenceType.TRAINING,
          AbsenceType.OTHER,
          AbsenceType.ACCIDENT,
          AbsenceType.COMP_TIME,
          AbsenceType.CHILD_SICK,
        ],
        {
          error: typeError({
            required: i18n._({
            id: 'absences.validation.typeRequired',
            message: 'Absence type is required',
          }),
          }),
        }
      ),
      fromDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'absences.validation.fromDateRequired',
          message: 'From date is required',
        }),
          invalid: i18n._({
          id: 'absences.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      toDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'absences.validation.toDateRequired',
          message: 'To date is required',
        }),
          invalid: i18n._({
          id: 'absences.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      fullDay: z.boolean().default(true),
      fromTime: z
        .string()
        .regex(TIME_REGEX, {
          message: i18n._({
            id: 'absences.validation.timeFormat',
            message: 'Time must be in HH:MM format',
          }),
        })
        .optional(),
      toTime: z
        .string()
        .regex(TIME_REGEX, {
          message: i18n._({
            id: 'absences.validation.timeFormat',
            message: 'Time must be in HH:MM format',
          }),
        })
        .optional(),
      notes: z.string().optional(),
      // currentUpdatedAt can be empty for newly created absences that were never updated
      // The backend optimistic lock check handles NULL values correctly
      currentUpdatedAt: z.string().optional(),
    })
    .refine(
      (data) => {
        return data.toDate >= data.fromDate;
      },
      {
        message: i18n._({
          id: 'absences.validation.toDateAfterFrom',
          message: 'To date must be on or after from date',
        }),
        path: ['toDate'],
      }
    )
    .refine(
      (data) => {
        if (!data.fullDay) {
          return !!data.fromTime && !!data.toTime;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'absences.validation.timesRequiredForPartialDay',
          message: 'Start and end times are required for partial-day absences',
        }),
        path: ['fromTime'],
      }
    )
    .refine(
      (data) => {
        if (
          !data.fullDay &&
          data.fromTime &&
          data.toTime &&
          data.fromDate.getTime() === data.toDate.getTime()
        ) {
          return data.toTime > data.fromTime;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'absences.validation.endTimeAfterStart',
          message: 'End time must be after start time',
        }),
        path: ['toTime'],
      }
    );

// Type inference for form data
export type CreateAbsenceFormData = z.infer<ReturnType<typeof getCreateAbsenceSchema>>;
/**
 * The schema's INPUT shape. Keys with a `.default()` are optional going in and
 * required coming out, so forms use `useForm<CreateAbsenceFormInput, unknown, CreateAbsenceFormData>`
 * — `zodResolver`'s `Resolver<Input, …, Output>` is not assignable to
 * `Resolver<Output, …, Output>`.
 */
export type CreateAbsenceFormInput = z.input<ReturnType<typeof getCreateAbsenceSchema>>;
export type UpdateAbsenceFormData = z.infer<ReturnType<typeof getUpdateAbsenceSchema>>;
/**
 * The schema's INPUT shape. Keys with a `.default()` are optional going in and
 * required coming out, so forms use `useForm<UpdateAbsenceFormInput, unknown, UpdateAbsenceFormData>`
 * — `zodResolver`'s `Resolver<Input, …, Output>` is not assignable to
 * `Resolver<Output, …, Output>`.
 */
export type UpdateAbsenceFormInput = z.input<ReturnType<typeof getUpdateAbsenceSchema>>;

/**
 * Transforms CreateAbsenceFormData to API request format.
 *
 * @param data - Form data from the create absence form
 * @returns CreateEmployeeAbsenceRequest payload for the API
 */
export function toCreateAbsenceRequest(
  data: CreateAbsenceFormData
): CreateEmployeeAbsenceRequest {
  return {
    employee_id: data.employeeId,
    absence_type: data.absenceType as CreateEmployeeAbsenceRequest['absence_type'],
    from_date: format(data.fromDate, 'yyyy-MM-dd'),
    to_date: format(data.toDate, 'yyyy-MM-dd'),
    full_day: data.fullDay,
    from_time: !data.fullDay ? data.fromTime : undefined,
    to_time: !data.fullDay ? data.toTime : undefined,
    notes: data.notes || undefined,
  };
}

/**
 * Transforms UpdateAbsenceFormData to API request format.
 *
 * @param data - Form data from the update absence form
 * @returns UpdateEmployeeAbsenceRequest payload for the API
 */
export function toUpdateAbsenceRequest(
  data: UpdateAbsenceFormData
): UpdateEmployeeAbsenceRequest {
  return {
    absence_type: data.absenceType as UpdateEmployeeAbsenceRequest['absence_type'],
    from_date: format(data.fromDate, 'yyyy-MM-dd'),
    to_date: format(data.toDate, 'yyyy-MM-dd'),
    full_day: data.fullDay,
    from_time: !data.fullDay ? data.fromTime : undefined,
    to_time: !data.fullDay ? data.toTime : undefined,
    notes: data.notes || undefined,
    // current_updated_at can be undefined for newly created absences
    // The backend optimistic lock check handles NULL values
    current_updated_at: data.currentUpdatedAt || undefined,
  };
}

/**
 * Converts an EmployeeAbsence response to form default values for the edit form.
 *
 * @param absence - Absence response from the API
 * @returns Default values for the UpdateAbsenceFormData form
 */
export function absenceToFormDefaults(absence: EmployeeAbsence): UpdateAbsenceFormData {
  return {
    absenceType:
      (absence.absence_type as UpdateAbsenceFormData['absenceType']) ||
      AbsenceType.VACATION,
    fromDate: absence.from_date ? new Date(absence.from_date) : new Date(),
    toDate: absence.to_date ? new Date(absence.to_date) : new Date(),
    fullDay: absence.full_day ?? true,
    fromTime: absence.from_time || undefined,
    toTime: absence.to_time || undefined,
    notes: absence.notes || '',
    currentUpdatedAt: absence.updated_at || '',
  };
}

/**
 * Creates the absence settings form schema (Settings -> Absences). The form is
 * flat; `AbsenceSettings.tsx` maps it to the nested PUT body. The ranges mirror
 * the backend (`models.MinCertificateDueDay` .. `MaxCertificateRetentionMonths`),
 * which validates them again.
 *
 * @param i18n - Lingui i18n instance for localized error messages
 * @returns Zod schema for the absence settings form
 */
export const getAbsenceSettingsSchema = (i18n: I18n) => {
  const dueDayMessage = i18n._({
    id: 'settings.absences.validation.dueDay',
    message: 'Enter a day from 1 to 30.',
  });
  const retentionMessage = i18n._({
    id: 'settings.absences.validation.retention',
    message: 'Enter 6 to 120 months.',
  });
  // An emptied number input arrives as undefined, which coerces to NaN: the
  // invalid-type message keeps that case on the same wording as the range.
  const dueDay = z.coerce
    .number<number>({ error: typeError({ required: dueDayMessage, invalid: dueDayMessage }) })
    .int({ message: dueDayMessage })
    .min(1, { message: dueDayMessage })
    .max(30, { message: dueDayMessage });

  return z.object({
    certificateMode: z.enum([CertificateMode.UPLOAD, CertificateMode.EAU]),
    dueDaySick: dueDay,
    dueDayAccident: dueDay,
    dueDayChildSick: dueDay,
    certificateRetentionMonths: z.coerce
      .number<number>({ error: typeError({ required: retentionMessage, invalid: retentionMessage }) })
      .int({ message: retentionMessage })
      .min(6, { message: retentionMessage })
      .max(120, { message: retentionMessage }),
  });
};

export type AbsenceSettingsFormData = z.infer<ReturnType<typeof getAbsenceSettingsSchema>>;
